import { useCallback, useEffect, useRef, useState } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { useGetLatestFortnoxImports } from '../api/FortnoxImportService/useGetLatestFortnoxImports';
import { useGetFortnoxImportRun } from '../api/FortnoxImportService/useGetFortnoxImportRun';
import { useStartFortnoxImport } from '../api/FortnoxImportService/useStartFortnoxImport';
import { useResumeFortnoxImport } from '../api/FortnoxImportService/useResumeFortnoxImport';
import { useRetryFailedFortnoxImport } from '../api/FortnoxImportService/useRetryFailedFortnoxImport';
import { useStopFortnoxImport } from '../api/FortnoxImportService/useStopFortnoxImport';
import { FortnoxImportRun, LatestFortnoxImportRuns } from '../models/FortnoxImportRun';
import { FortnoxIntegrationType } from '../types/backendTypes/FortnoxIntegrationType';
import { buildFortnoxImportAlert } from '../utils/fortnoxImportMessage';
import {
  fortnoxImportReadyToWork,
  fortnoxImportProgress,
  fortnoxImportSummaryOf,
  isFortnoxImportActive,
} from '../utils/fortnoxImportRun';

/** How often a running import is read again. */
export const FORTNOX_IMPORT_POLL_MS = 5_000;

/** How often a stalled import is read again: it changes only when someone resumes it. */
export const FORTNOX_IMPORT_STALLED_POLL_MS = 60_000;

/**
 * Follows the organization's Fortnox imports (#686).
 *
 * An import runs as a job in the backend and can take hours, so nothing here waits for it. The
 * latest run per integration type is read when the organization is known, and every active run
 * is read again every few seconds until it finishes. The tab can be closed at any time: opening
 * the app again picks the running import up from the backend.
 *
 * A run that finishes while it is being followed is reported with the same message the
 * synchronous import used, so a partial import is never shown as a complete one.
 */
export const useFortnoxImports = (enabled: boolean) => {
  const [runs, setRuns] = useState<LatestFortnoxImportRuns>({
    [FortnoxIntegrationType.Base]: null,
    [FortnoxIntegrationType.Salary]: null,
  });
  const { alert } = useAlert();
  const { action: getLatest } = useGetLatestFortnoxImports();
  const { action: getRun } = useGetFortnoxImportRun();
  const { action: startImport } = useStartFortnoxImport();
  const { action: resumeImport } = useResumeFortnoxImport();
  const { action: retryFailedImport } = useRetryFailedFortnoxImport();
  const { action: stopImport } = useStopFortnoxImport();

  // When each run last changed, by id, as the app saw it. A job that is alive moves its counts or
  // its heartbeat; when neither changes, the loader says so long before the run counts as stalled.
  const [lastChangeAt, setLastChangeAt] = useState<Record<string, number>>({});
  const lastSeen = useRef(new Map<string, string>());

  // Runs seen active in this session. Only their finish is reported; an import that ended
  // before the page was opened is shown on the settings page, not announced again.
  const followed = useRef(new Set<string>());

  const report = useCallback(
    (run: FortnoxImportRun) => {
      if (run.status === 'failed') {
        alert('error', 'Importen från Fortnox kunde inte startas. Försök igen.');
        return;
      }
      if (run.status === 'stopped') {
        alert(
          'info',
          'Importen från Fortnox stoppades. Det som hann hämtas finns kvar, och importen kan återupptas under Inställningar.'
        );
        return;
      }
      const summary = fortnoxImportSummaryOf(run);
      if (summary) {
        const { severity, message, seconds } = buildFortnoxImportAlert(summary);
        alert(severity, message, undefined, seconds);
      }
    },
    [alert]
  );

  // Runs already announced as ready to work, so the message shows once per run.
  const announcedReady = useRef(new Set<string>());

  const track = useCallback(
    (run: FortnoxImportRun) => {
      if (
        followed.current.has(run.id) &&
        fortnoxImportReadyToWork(run) &&
        !announcedReady.current.has(run.id)
      ) {
        announcedReady.current.add(run.id);
        alert(
          'success',
          'Kunder och artiklar från Fortnox är klara, ni kan börja arbeta. Fakturor hämtas fortfarande i bakgrunden.',
          undefined,
          20
        );
      }
      setRuns((current) => ({ ...current, [run.integrationType]: run }));
      const { processed, total } = fortnoxImportProgress(run);
      const state = `${run.status}:${processed}:${total}:${run.heartbeatAt}`;
      if (lastSeen.current.get(run.id) !== state) {
        lastSeen.current.set(run.id, state);
        setLastChangeAt((current) => ({ ...current, [run.id]: Date.now() }));
      }
      if (isFortnoxImportActive(run)) {
        followed.current.add(run.id);
      } else if (followed.current.delete(run.id)) {
        report(run);
      }
    },
    [report, alert]
  );

  useEffect(() => {
    if (!enabled) return;
    getLatest()
      .then((latest) => Object.values(latest).forEach((run) => run && track(run)))
      .catch(() => undefined);
  }, [enabled]);

  const activeRuns = Object.values(runs).filter(isFortnoxImportActive) as FortnoxImportRun[];
  // A stalled run changes only when someone resumes it, possibly from another browser, so it is
  // still read, just rarely.
  const pollMs = activeRuns.every((run) => run.stalled)
    ? FORTNOX_IMPORT_STALLED_POLL_MS
    : FORTNOX_IMPORT_POLL_MS;
  // The ids and statuses of the active runs, so the interval restarts only when they change.
  const activeKey = activeRuns.map((run) => `${run.id}:${run.status}:${run.stalled}`).join(',');

  useEffect(() => {
    if (!activeKey) return;
    const ids = activeKey.split(',').map((entry) => entry.split(':')[0]);
    const poll = () =>
      ids.forEach((id) =>
        getRun(id)
          .then(track)
          .catch(() => undefined)
      );
    // Nobody is looking at a hidden tab. Coming back to it reads the runs at once.
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') poll();
    }, pollMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [activeKey, pollMs]);

  /** Starts an import, or follows the one already running for the integration type. */
  const start = async (integrationType: FortnoxIntegrationType, full = false) => {
    try {
      const result = await startImport(integrationType, full);
      track('runningRunId' in result ? await getRun(result.runningRunId) : result);
    } catch {
      // useAPI has shown the error.
    }
  };

  const resume = async (integrationType: FortnoxIntegrationType) => {
    const run = runs[integrationType];
    if (!run) return;
    try {
      track(await resumeImport(run.id));
    } catch {
      // useAPI has shown the error.
    }
  };

  const stop = async (integrationType: FortnoxIntegrationType) => {
    const run = runs[integrationType];
    if (!run) return;
    try {
      track(await stopImport(run.id));
    } catch {
      // useAPI has shown the error.
    }
  };

  const retryFailed = async (integrationType: FortnoxIntegrationType) => {
    const run = runs[integrationType];
    if (!run) return;
    try {
      track(await retryFailedImport(run.id));
    } catch {
      // useAPI has shown the error.
    }
  };

  return { runs, lastChangeAt, start, resume, retryFailed, stop };
};
